/* ==========================================================================
   paymentService

   This is intentionally provider-agnostic. Wallet funding is the only flow
   that talks to an external payment provider (the payment provider) — Buy
   Electricity and Bill Centre both settle against the wallet balance, not
   the provider directly (see architecture note in the project brief).

   PER THIS ROUND OF WORK: the payment provider is NOT wired up yet. initPayment /
   getPaymentStatus below only simulate the provider round trip so the
   Fund Wallet UI has real pending/success/failed states to work against.
   This file checks config.paymentProviderConnected, NOT
   config.useMockServices — the data backend (Supabase) can go live while
   this stays simulated, so funding still runs through the simulated round
   trip below even in real mode, until that flag flips. When the the payment provider
   integration milestone starts, only this file's mock branches change —
   walletService and the Fund Wallet UI should not need to change at all,
   because they only depend on the shapes in src/types (PaymentInitResult,
   PaymentStatusResult).

   FUTURE BACKEND ENDPOINTS (backend, not frontend, talks to the payment provider):
     POST /payments/initiate     { amountKobo, purpose: "wallet_funding" }
                                  -> PaymentInitResult { paymentReference, checkoutUrl, status }
     GET  /payments/:reference   -> PaymentStatusResult { status, failureReason? }

   The frontend never receives or stores a the payment provider secret key or
   webhook signing secret — only a public/publishable key (a public key,
   currently unset) would ever appear in the bundle, and only once that
   milestone starts.
   ========================================================================== */

import { config } from "../config";
import { apiClient } from "./apiClient";
import { delay, genReference, shouldSimulateFailure } from "./mock/mockUtils";
import type { PaymentInitResult, PaymentStatusResult } from "../types";

async function initiatePayment(amountKobo: number): Promise<PaymentInitResult> {
  if (!config.paymentProviderConnected) {
    await delay(500);
    return {
      paymentReference: genReference("PAY"),
      provider: "mock",
      checkoutUrl: null,
      status: "pending",
    };
  }
  return apiClient.request<PaymentInitResult>("/payments/initiate", {
    method: "POST",
    body: { amountKobo, purpose: "wallet_funding" },
  });
}

async function getPaymentStatus(reference: string): Promise<PaymentStatusResult> {
  if (!config.paymentProviderConnected) {
    await delay(900);
    if (shouldSimulateFailure()) {
      return { paymentReference: reference, status: "failed", failureReason: "Payment declined by provider (simulated)." };
    }
    return { paymentReference: reference, status: "success" };
  }
  return apiClient.request<PaymentStatusResult>(`/payments/${reference}`);
}

export const paymentService = { initiatePayment, getPaymentStatus };
